import { useEffect, useMemo, useState } from "react";
import autosize from "autosize";
import {
  buildMentionOptions,
  deleteMentionToken,
  getActiveMention,
  getMentionTokenBeforeCaret,
  insertMention,
  syncMentionData,
} from "../util/mentions";

export function useMentionComposer({
  messageContent,
  setMessageContent,
  setSelectedMentionData,
  textareaRef,
  members,
  includeAiCommand,
  onlineUsers,
  onSelectOption,
}) {
  const [mentionSession, setMentionSession] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const onlineUserIds = useMemo(() => {
    const ids = new Set();
    (onlineUsers || []).forEach((user) => {
      if (user?.is_online && user.user_id != null) ids.add(user.user_id);
    });
    return ids;
  }, [onlineUsers]);

  const mentionOptions = useMemo(() => {
    if (!mentionSession || mentionSession.dismissed) return [];
    return buildMentionOptions({
      members,
      query: mentionSession.query,
      includeAiCommand,
      onlineUserIds,
    });
  }, [mentionSession, members, includeAiCommand, onlineUserIds]);

  const queryKey =
    mentionSession && !mentionSession.dismissed
      ? `${mentionSession.start}:${mentionSession.query}`
      : "";

  useEffect(() => {
    setActiveIndex(0);
  }, [queryKey]);

  useEffect(() => {
    setActiveIndex((index) => {
      if (mentionOptions.length === 0) return 0;
      return Math.min(index, mentionOptions.length - 1);
    });
  }, [mentionOptions.length]);

  const placeCaret = (caret) => {
    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      const nextCaret = Math.max(0, Math.min(caret, textarea.value.length));
      textarea.setSelectionRange(nextCaret, nextCaret);
      autosize.update(textarea);
    });
  };

  const syncSession = (value, caret) => {
    const next = getActiveMention(value, caret);
    setMentionSession((prev) => {
      if (!next) return null;
      const same =
        prev &&
        prev.start === next.start &&
        prev.query === next.query &&
        prev.end === next.end;
      return { ...next, dismissed: Boolean(same && prev.dismissed) };
    });
  };

  const handleChange = (event) => {
    const value = event.target.value;
    const caret = event.target.selectionStart ?? value.length;
    setMessageContent(value);
    setSelectedMentionData((prev) => syncMentionData(value, prev));
    syncSession(value, caret);
  };

  const handleCaretMove = (event) => {
    if (
      event.key === "ArrowDown" ||
      event.key === "ArrowUp" ||
      event.key === "Enter" ||
      event.key === "Tab" ||
      event.key === "Escape"
    ) {
      return;
    }

    const value = event.target.value;
    const caret = event.target.selectionStart ?? value.length;
    syncSession(value, caret);
  };

  const handleSelect = (option) => {
    if (!option || !mentionSession || mentionSession.dismissed) return;
    const { text, caret } = insertMention(
      messageContent,
      mentionSession,
      option.name,
    );
    setMessageContent(text);
    setSelectedMentionData((prev) => {
      const exists = prev.some((item) => String(item.id) === String(option.id));
      const next = exists
        ? prev
        : [...prev, { id: option.id, name: option.name }];
      return syncMentionData(text, next);
    });
    setMentionSession(null);
    placeCaret(caret);
    onSelectOption?.(option);
  };

  const handleKeyDown = (event) => {
    const textarea = event.target;
    const caret = textarea.selectionStart ?? 0;
    const selectionCollapsed = textarea.selectionStart === textarea.selectionEnd;

    if (
      event.key === "Backspace" &&
      selectionCollapsed &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey
    ) {
      const token = getMentionTokenBeforeCaret(textarea.value, caret);
      if (token) {
        event.preventDefault();
        const next = deleteMentionToken(textarea.value, token);
        setMessageContent(next.text);
        setSelectedMentionData((prev) => syncMentionData(next.text, prev));
        syncSession(next.text, next.caret);
        placeCaret(next.caret);
        return true;
      }
    }

    const menuOpen = Boolean(mentionSession && !mentionSession.dismissed);
    if (!menuOpen) return false;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (mentionOptions.length > 0) {
        setActiveIndex((index) => (index + 1) % mentionOptions.length);
      }
      return true;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (mentionOptions.length > 0) {
        setActiveIndex(
          (index) =>
            (index - 1 + mentionOptions.length) % mentionOptions.length,
        );
      }
      return true;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setMentionSession((prev) => (prev ? { ...prev, dismissed: true } : prev));
      return true;
    }

    if ((event.key === "Enter" && !event.shiftKey) || event.key === "Tab") {
      if (mentionOptions.length > 0) {
        event.preventDefault();
        const option =
          mentionOptions[Math.min(activeIndex, mentionOptions.length - 1)];
        handleSelect(option);
        return true;
      }
    }

    return false;
  };

  const handleBlur = () => {
    setMentionSession((prev) => (prev ? { ...prev, dismissed: true } : prev));
  };

  const handleFocus = (event) => {
    const value = event.target.value;
    const caret = event.target.selectionStart ?? value.length;
    const next = getActiveMention(value, caret);
    if (next) setMentionSession({ ...next, dismissed: false });
  };

  const reset = () => {
    setMentionSession(null);
    setActiveIndex(0);
    requestAnimationFrame(() => {
      if (textareaRef.current) autosize.update(textareaRef.current);
    });
  };

  return {
    mentionSession,
    mentionOptions,
    activeIndex,
    setActiveIndex,
    pickerOpen: Boolean(mentionSession && !mentionSession.dismissed),
    handleChange,
    handleCaretMove,
    handleKeyDown,
    handleSelect,
    handleBlur,
    handleFocus,
    reset,
  };
}
